import { useQuery } from '@tanstack/react-query'
import { TrimStatus } from 'components/cattles/trim/models'
import {
	IFiltersLamenessTable,
	InspectionStatus,
	TreatmentStatus,
} from 'models'
import moment from 'moment'
import { Dispatch, useEffect, useLayoutEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import CattlesApiService from 'services/cattlesApiServices'
import { useAppDispatch } from 'store/hooks'
import { notifyUser } from 'store/notificationsSlice'
import { useGetAvailableCowTags } from './query/useGetAvailableCowTags'
import { defaultInitialState } from 'store/lamenessFilterSlice'

type TabsDescriptionType = {
	key: string
	title: string
	description: string | null
}

interface ICowsNextPageProps {
	page: number
	setPage?: Dispatch<number>
	activeFilter?: any
	initialPageSize: number
	fatnessPoint?: number[]
	params?: IFiltersLamenessTable
	isFetchCowsListEnabled?: boolean
	isFetchComplete?: boolean
	initialTabs?: TabsDescriptionType[]
	tag_id_search?: string
	identified?: boolean
	ordering?: string | null
}

const TAB_INSPECTION_KEYS = [InspectionStatus.Planned, InspectionStatus.Expired]
const TAB_TREATMENT_KEYS = [TreatmentStatus.DisposeRecommended]

const getFiltersWithoutTabStatuses = (
	params?: IFiltersLamenessTable,
): IFiltersLamenessTable => {
	const source = params ?? defaultInitialState

	return {
		...source,
		inspectionStatus: {
			...source.inspectionStatus,
			statuses: source.inspectionStatus.statuses.filter(
				status => !TAB_INSPECTION_KEYS.includes(status),
			),
		},
		treatmentStatus: {
			...source.treatmentStatus,
			statuses: source.treatmentStatus.statuses.filter(
				status => !TAB_TREATMENT_KEYS.includes(status),
			),
		},
	}
}

const formatTabCount = (count: number) => (count > 0 ? String(count) : null)

const useCowsNextPage = (props: ICowsNextPageProps) => {
	const {
		page,
		activeFilter,
		initialPageSize,
		fatnessPoint,
		params,
		isFetchComplete,
		isFetchCowsListEnabled,
		initialTabs,
		tag_id_search,
		identified,
		ordering,
	} = props
	const location = useLocation()
	const [cowsList, setCowsList] = useState([])
	const [loadedPage, setLoadedPage] = useState(page)
	const [totalCountPages, setTotalCountPages] = useState<number>(1)
	const { data: availableTags, isLoading: isAvailableTagsLoading } =
		useGetAvailableCowTags()
	const [availableTabs, setAvailableTabs] = useState(initialTabs)
	const dispatch = useAppDispatch()

	const hasPlannedTab = initialTabs?.some(
		tab => tab.key === InspectionStatus.Planned,
	)
	const hasExpiredTab = initialTabs?.some(
		tab => tab.key === InspectionStatus.Expired,
	)
	const hasDisposeTab = initialTabs?.some(
		tab => tab.key === TreatmentStatus.DisposeRecommended,
	)
	const tabCountFilters = getFiltersWithoutTabStatuses(params)
	const tabCountQueryEnabled =
		isFetchCowsListEnabled && isFetchComplete && Boolean(params)

	const updateTabsDescription = (examinationRequired, trimMissed) => {
		setAvailableTabs(tabs =>
			tabs?.map(tab => {
				switch (tab.key) {
					case InspectionStatus.Required:
						return {
							...tab,
							description:
								examinationRequired > 0 ? examinationRequired : tab.description,
						}
					case TrimStatus.Missed:
						return {
							...tab,
							description: trimMissed > 0 ? trimMissed : tab.description,
						}
					default:
						return tab
				}
			}),
		)
	}

	const {
		data,
		isPending: fetchingCowsList,
		isSuccess,
		isError,
	} = useQuery({
		queryKey: [
			'cowsList',
			page,
			initialPageSize,
			activeFilter,
			fatnessPoint,
			params,
			tag_id_search,
			identified,
			ordering,
		],
		queryFn: () =>
			CattlesApiService.getCowsList(
				activeFilter === 'all' || !activeFilter ? null : activeFilter,
				fatnessPoint,
				page,
				initialPageSize,
				params,
				tag_id_search,
				identified,
				ordering,
			),
		enabled: isFetchCowsListEnabled && isFetchComplete,
		staleTime: 0,
		refetchOnWindowFocus: true,
	})

	const { data: plannedCountData } = useQuery({
		queryKey: [
			'cowsTabCount',
			InspectionStatus.Planned,
			tabCountFilters,
			tag_id_search,
		],
		queryFn: () =>
			CattlesApiService.getCowsCount(
				{
					...tabCountFilters,
					inspectionStatus: {
						...tabCountFilters.inspectionStatus,
						statuses: [InspectionStatus.Planned],
					},
				},
				undefined,
				tag_id_search,
			),
		enabled: !!hasPlannedTab && tabCountQueryEnabled,
		staleTime: 0,
		refetchOnWindowFocus: true,
	})

	const { data: expiredCountData } = useQuery({
		queryKey: [
			'cowsTabCount',
			InspectionStatus.Expired,
			tabCountFilters,
			tag_id_search,
		],
		queryFn: () =>
			CattlesApiService.getCowsCount(
				{
					...tabCountFilters,
					inspectionStatus: {
						...tabCountFilters.inspectionStatus,
						statuses: [InspectionStatus.Expired],
					},
				},
				undefined,
				tag_id_search,
			),
		enabled: !!hasExpiredTab && tabCountQueryEnabled,
		staleTime: 0,
		refetchOnWindowFocus: true,
	})

	const { data: disposeCountData } = useQuery({
		queryKey: [
			'cowsTabCount',
			TreatmentStatus.DisposeRecommended,
			tabCountFilters,
			tag_id_search,
		],
		queryFn: () =>
			CattlesApiService.getCowsCount(
				{
					...tabCountFilters,
					treatmentStatus: {
						...tabCountFilters.treatmentStatus,
						statuses: [TreatmentStatus.DisposeRecommended],
					},
				},
				undefined,
				tag_id_search,
			),
		enabled: !!hasDisposeTab && tabCountQueryEnabled,
		staleTime: 0,
		refetchOnWindowFocus: true,
	})

	useLayoutEffect(() => {
		if (isSuccess) {
			const { count, results, examination_required, trim_missed } = data.data

			setTotalCountPages(count)
			setCowsList(results)
			setLoadedPage(page)
			updateTabsDescription(examination_required, trim_missed)

			const isMissedTrim = results.some(cow =>
				moment().isAfter(cow.trim_next_dt),
			)
			if (isMissedTrim && location.pathname === '/cattles/trim') {
				dispatch(
					notifyUser({
						title: 'Пропущена обрезка копыт',
						description: 'Не проведена запланированная обрезка копыт.',
						isError: true,
					}),
				)
			}
		}
	}, [data, dispatch, isSuccess, location.pathname, page])

	useEffect(() => {
		if (plannedCountData?.data != null) {
			setAvailableTabs(tabs =>
				tabs?.map(tab =>
					tab.key === InspectionStatus.Planned
						? {
								...tab,
								description: formatTabCount(plannedCountData.data),
							}
						: tab,
				),
			)
		}
	}, [plannedCountData])

	useEffect(() => {
		if (expiredCountData?.data != null) {
			setAvailableTabs(tabs =>
				tabs?.map(tab =>
					tab.key === InspectionStatus.Expired
						? {
								...tab,
								description: formatTabCount(expiredCountData.data),
							}
						: tab,
				),
			)
		}
	}, [expiredCountData])

	useEffect(() => {
		if (disposeCountData?.data != null) {
			setAvailableTabs(tabs =>
				tabs?.map(tab =>
					tab.key === TreatmentStatus.DisposeRecommended
						? {
								...tab,
								description: formatTabCount(disposeCountData.data),
							}
						: tab,
				),
			)
		}
	}, [disposeCountData])

	useEffect(() => {
		if (isError) {
			dispatch(
				notifyUser({
					title: 'Ошибка при формировании списка коров',
					description: 'Попробуйте обновить страницу',
					isError: true,
				}),
			)
		}
	}, [dispatch, isError])

	return {
		cowsList,
		totalCountPages,
		fetchingCowsList: fetchingCowsList || isAvailableTagsLoading,
		availableTags: availableTags.availableTagsOption ?? [],
		availableTabs,
		loadedPage,
		isCowsListError: isError,
	}
}

export default useCowsNextPage
